import type {
    SparklineDomainConstantType,
    SparklineIndexConstantType,
    SparklineShapeConstantType,
    SparklineShape
} from "./type";
import {
    getChartLinearScale,
    getChartCurvePath,
    getChartAreaPath,
    getChartBarPath,
    getChartPointX
} from "../chart/util";

export const getSparklineDomain = ({
    variant,
    values
}: SparklineDomainConstantType): [number, number] => {
    const min = Math.min(...values);

    const max = Math.max(...values);

    if(variant === "bars") {
        const low = Math.min(0, min);

        const high = Math.max(0, max);

        return high === low ? [
            low,
            low + 1
        ] : [
            low,
            high
        ];
    }

    return max === min ? [
        min - 1,
        max + 1
    ] : [
        min,
        max
    ];
};

export const getSparklineIndex = ({
    points,
    x
}: SparklineIndexConstantType): number | undefined => {
    let best: number | undefined = undefined;

    points.forEach((point, index) => {
        const current = best === undefined ? undefined : points[best];

        if(!current || Math.abs(point.x - x) < Math.abs(current.x - x)) {
            best = index;
        }
    });

    return best;
};

export const getSparklineShape = ({
    getBarProgress,
    measures,
    variant,
    height,
    values,
    isRTL,
    width,
    high,
    low
}: SparklineShapeConstantType): SparklineShape => {
    if(!values.length || width <= 0 || height <= 0) {
        return {
            baseline: height,
            points: [],
            area: "",
            bars: [],
            line: ""
        };
    }

    const {
        barRadius,
        lineWidth,
        barRatio,
        barMax,
        inset
    } = measures;

    const count = values.length;

    if(variant === "bars") {
        const toY = getChartLinearScale({
            range: [
                height,
                0
            ],
            domain: [
                low,
                high
            ]
        });

        const zero = toY(0);

        const slot = width / count;

        const barWidth = Math.min(barMax, slot * barRatio);

        const bars = values.map((value, index) => {
            const slotIndex = isRTL ? count - 1 - index : index;

            const x = slotIndex * slot + (slot - barWidth) / 2;

            const progress = getBarProgress?.(index) ?? 1;

            const size = Math.max(lineWidth, Math.abs(toY(value) - zero)) * progress;

            const isNegative = value < 0;

            return {
                path: getChartBarPath({
                    side: isNegative ? "bottom" : "top",
                    y: isNegative ? zero : zero - size,
                    radius: barRadius,
                    width: barWidth,
                    height: size,
                    x
                })
            };
        });

        return {
            points: values.map((value, index) => ({
                x: (isRTL ? count - 1 - index : index) * slot + slot / 2,
                y: toY(value)
            })),
            baseline: zero,
            area: "",
            line: "",
            bars
        };
    }

    const toY = getChartLinearScale({
        range: [
            height - inset,
            inset
        ],
        domain: [
            low,
            high
        ]
    });

    const points = values.map((value, index) => ({
        x: getChartPointX({
            size: width - inset * 2,
            start: inset,
            count,
            index,
            isRTL
        }),
        y: toY(value)
    }));

    const baseline = height - inset;

    return {
        area: variant === "area" ? getChartAreaPath({
            curve: "natural",
            bottom: height,
            top: points
        }) : "",
        line: getChartCurvePath({
            curve: "natural",
            points
        }),
        bars: [],
        baseline,
        points
    };
};
